import { type FC, type ReactNode } from 'react';

import {
  Button,
  Icon,
  LoadingIcon,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  Stack,
  Text,
} from '@eluve/components';

import type { MiniAppContext } from '../../../types';

import { FindingsReview } from './FindingsReview';
import { shouldBlockForFindings } from './findings-review-gate-state';
import { useFindingsReview } from './useFindingsReview';

export const FindingsReviewGate: FC<{
  context: MiniAppContext;
  children: ReactNode;
  contentReady?: boolean;
  sectionsOwnReview?: boolean;
}> = ({
  context,
  children,
  contentReady = false,
  sectionsOwnReview = false,
}) => {
  const review = useFindingsReview(context);

  if (shouldBlockForFindings({ phase: review.phase, contentReady })) {
    return (
      <Stack className="py-10" align="center" gap={2}>
        <LoadingIcon />
        <Text size="sm" color="secondary">
          Preparing findings from this session…
        </Text>
      </Stack>
    );
  }

  if (review.phase === 'preparing') return children;

  if (review.phase === 'review' && !sectionsOwnReview) {
    return <FindingsReview context={context} review={review} />;
  }

  return (
    <Stack gap={4}>
      {review.phase === 'applied' || review.phase === 'review' ? (
        <Sheet>
          <Stack direction="horizontal" fullWidth={false}>
            <SheetTrigger asChild>
              <Button variant="outline" size="sm">
                <Icon name="FileText" size="sm" />
                View extraction
              </Button>
            </SheetTrigger>
          </Stack>
          <SheetContent
            side="right"
            className="w-2xl max-w-[92vw] overflow-y-auto"
          >
            <SheetHeader>
              <SheetTitle>Extraction</SheetTitle>
              <SheetDescription>
                Approve, edit, or reject — decisions apply to the care plan.
              </SheetDescription>
            </SheetHeader>
            <div className="px-4 pb-6">
              <FindingsReview
                context={context}
                review={review}
                showApply={false}
              />
            </div>
          </SheetContent>
        </Sheet>
      ) : (
        <Text size="sm" color="tertiary">
          No new findings surfaced from this visit.
        </Text>
      )}

      {children}
    </Stack>
  );
};
